import { BlogPost, BlogPostMetadata } from '@/types/blog';

const frontMatterPattern = /^---\s*\n([\s\S]*?)\n\s*---\s*\n([\s\S]*)$/;

function createCanonicalBlogDateFromPath(year: string, month: string, day: string): string {
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toISOString();
}

function normalizeDateOnly(dateString: string): string | null {
  if (!dateString.trim()) {
    return null;
  }

  const dateOnlyMatch = dateString.trim().match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[1];
  }

  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toISOString().slice(0, 10);
}

function normalizeMarkdown(markdown: string): string {
  return markdown.trim().replace(/^\uFEFF/, '');
}

function hasFrontMatter(markdown: string): boolean {
  return frontMatterPattern.test(normalizeMarkdown(markdown));
}

// Simple browser-compatible reading time estimator (words per minute)
export function calculateReadingTime(text: string) {
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  
  return {
    text: `${minutes} min${minutes !== 1 ? 's' : ''} read`,
    minutes,
    time: minutes * 60 * 1000,
    words: wordCount
  };
}

// Browser-compatible function to parse markdown files with front matter
export function parseMarkdown(markdown: string): BlogPost {
  // Remove any BOM or other invisible characters that might affect parsing
  const cleanMarkdown = normalizeMarkdown(markdown);
  
  // Simple front matter parser that works in browser
  const match = cleanMarkdown.match(frontMatterPattern);
  
  if (!match) {
    console.error("No front matter found in markdown:", cleanMarkdown.substring(0, 100));
    // No front matter found, return basic post
    return {
      title: 'Untitled Post',
      description: '',
      date: new Date().toISOString(),
      thumbnail: '/placeholder.svg',
      tags: [],
      slug: '',
      content: cleanMarkdown,
      readingTime: calculateReadingTime(cleanMarkdown)
    };
  }
  
  // Extract front matter and content
  const frontMatter = match[1];
  const content = match[2];
  
  console.log("Front matter found:", frontMatter);
  
  // Parse front matter (key-value pairs)
  const data: Record<string, any> = {};
  const frontMatterLines = frontMatter.split('\n');
  
  for (const line of frontMatterLines) {
    if (!line.trim()) continue; // Skip empty lines
    
    const colonIndex = line.indexOf(':');
    if (colonIndex !== -1) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();
      
      // Remove quotes if present
      if (value.startsWith('"') && value.endsWith('"')) {
        value = value.slice(1, -1);
      }
      
      // Handle arrays (tags)
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          // Try to parse as JSON
          data[key] = JSON.parse(value);
        } catch {
          // Fallback: simple comma-separated parsing
          data[key] = value
            .slice(1, -1)
            .split(',')
            .map(item => {
              const trimmed = item.trim();
              // Remove quotes if present
              return trimmed.startsWith('"') && trimmed.endsWith('"') 
                ? trimmed.slice(1, -1) 
                : trimmed;
            });
        }
      } else {
        data[key] = value;
      }
    }
  }
  
  // Calculate reading time
  const readingTimeResult = calculateReadingTime(content);
  
  // Create blog post with parsed front matter
  const post: BlogPost = {
    title: data.title || 'Untitled Post',
    description: data.description || '',
    date: data.date ? new Date(data.date).toISOString() : new Date().toISOString(),
    thumbnail: data.thumbnail || '/placeholder.svg',
    tags: data.tags || [],
    slug: data.slug || '',
    content,
    readingTime: readingTimeResult
  };
  
  console.log("Parsed blog post:", post);
  
  return post;
}

// Extract date components from ISO date string for URL path
export function getDatePathFromIsoString(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  return `${year}/${month}/${day}`;
}

export function getStaticPostForPath(year: string, month: string, day: string, slug: string): BlogPost | null {
  if (typeof window === 'undefined' || !window.__STATIC_BLOG_POST__) {
    return null;
  }

  const staticPost = window.__STATIC_BLOG_POST__;
  const staticDatePath = getDatePathFromIsoString(staticPost.date);
  const requestedDatePath = `${year}/${month}/${day}`;

  if (staticPost.slug === slug && staticDatePath === requestedDatePath) {
    return staticPost;
  }

  console.warn(
    `Ignoring static blog post data for ${staticDatePath}/${staticPost.slug}; current route is ${requestedDatePath}/${slug}`
  );

  return null;
}

// Client-side: Fetch all blog posts metadata from the pre-generated file
export async function getAllPosts(): Promise<BlogPostMetadata[]> {
  try {
    // Fetch the pre-generated metadata file
    const response = await fetch('/api/blog/posts.json');
    if (!response.ok) {
      const status = response.status;
      const message = await response.text().catch(() => 'Unknown error');
      
      console.error(`Failed to fetch blog posts metadata (${status}): ${message}`);
      console.error('Make sure to run the build script which generates the posts.json file');
      throw new Error(`Failed to fetch blog posts metadata: ${status} ${message}`);
    }
    
    const posts = await response.json();
    return posts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    console.error('If building locally, run: npm run generate-blog-metadata');
    return [];
  }
}

// Client-side: Fetch a specific blog post by its date path and slug
export async function getPostByPath(year: string, month: string, day: string, slug: string): Promise<BlogPost | null> {
  try {
    const staticPost = getStaticPostForPath(year, month, day, slug);
    if (staticPost) {
      console.log('Using static blog post data from window.__STATIC_BLOG_POST__');
      return staticPost;
    }
    
    console.log(`Fetching markdown file for ${year}/${month}/${day}/${slug}`);

    const locations = [
      `/blog/posts/${year}/${month}/${day}/${slug}.md`,
      `/blog/${year}/${month}/${day}/${slug}.md`,
      `/blog/${slug}.md`,
    ];

    for (const location of locations) {
      const response = await fetch(location);

      if (!response.ok) {
        continue;
      }

      const contentType = response.headers.get('content-type') || '';
      const markdown = await response.text();

      if (contentType.includes('text/html') || !hasFrontMatter(markdown)) {
        console.warn(`Ignoring non-markdown response from ${location}`);
        continue;
      }

      console.log(`Fetched markdown content successfully from ${location}`);

      // Parse the markdown content with our custom browser-compatible parser
      const post = parseMarkdown(markdown);

      const canonicalPathDate = createCanonicalBlogDateFromPath(year, month, day);
      const frontMatterDate = normalizeDateOnly(post.date);
      const expectedDate = canonicalPathDate.slice(0, 10);
      if (frontMatterDate && frontMatterDate !== expectedDate) {
        console.warn(
          `Overriding frontmatter date ${frontMatterDate} with route date ${expectedDate} for ${year}/${month}/${day}/${slug}`
        );
      }

      post.date = canonicalPathDate;

      // Update the slug from the URL if missing
      if (!post.slug) {
        post.slug = slug;
      }

      return post;
    }

    console.error(`Failed to fetch blog post at any known location`);
    throw new Error(`Blog post not found: ${year}/${month}/${day}/${slug}`);
  } catch (error) {
    console.error(`Error fetching blog post ${year}/${month}/${day}/${slug}:`, error);
    return null;
  }
}

// Helper to format date for display
export function formatBlogDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function normalizeBlogTagValue(tag: string): string {
  return tag.trim().toLowerCase();
}

export function slugifyBlogTag(tag: string): string {
  return normalizeBlogTagValue(tag).replace(/\s+/g, '-');
}
